import { absoluteToCanonical, canonicalToAbsolute, squareName } from "./action";
import { adjudicate, PgxChess } from "./environment";
import { Piece, type ChessState, type Color } from "./types";

const PIECES: Record<string, number> = {
  p: Piece.Pawn, n: Piece.Knight, b: Piece.Bishop,
  r: Piece.Rook, q: Piece.Queen, k: Piece.King,
};

export function fromFen(fen: string): ChessState {
  const fields = fen.trim().split(/\s+/);
  if (fields.length < 4) throw new RangeError("FEN requires at least four fields");
  const ranks = fields[0]!.split("/");
  if (ranks.length !== 8) throw new RangeError("FEN board requires eight ranks");
  const color: Color = fields[1] === "b" ? 1 : fields[1] === "w" ? 0 : (() => {
    throw new RangeError("Invalid FEN active color");
  })();
  const absolute = new Int8Array(64);
  ranks.forEach((rankText, fenRank) => {
    let file = 0;
    for (const char of rankText) {
      if (/\d/.test(char)) {
        file += Number(char);
      } else {
        const piece = PIECES[char.toLowerCase()];
        if (!piece || file >= 8) throw new RangeError("Invalid FEN board");
        absolute[file * 8 + (7 - fenRank)] = char === char.toUpperCase() ? piece : -piece;
        file += 1;
      }
    }
    if (file !== 8) throw new RangeError("Invalid FEN rank width");
  });
  const board = new Int8Array(64);
  for (let square = 0; square < 64; square += 1) {
    const canonical = absoluteToCanonical(square, color);
    board[canonical] = color === 0 ? absolute[square]! : -absolute[square]!;
  }
  const castling = fields[2]!;
  const absoluteRights = [castling.includes("Q"), castling.includes("K"), castling.includes("q"), castling.includes("k")];
  const rights = color === 0
    ? absoluteRights
    : [absoluteRights[2]!, absoluteRights[3]!, absoluteRights[0]!, absoluteRights[1]!];
  let enPassant = -1;
  if (fields[3] !== "-") {
    const ep = fields[3]!;
    const absoluteSquare = (ep.charCodeAt(0) - 97) * 8 + Number(ep[1]) - 1;
    enPassant = absoluteToCanonical(absoluteSquare, color);
  }
  // Reuse the environment's validated initializer shape, replacing debug fields.
  const initial = new PgxChess().init();
  return adjudicate({
    ...initial,
    color,
    board,
    boardHistory: [board.slice()],
    castlingRights: rights as [boolean, boolean, boolean, boolean],
    enPassant,
    halfmoveCount: Number(fields[4] ?? 0),
    fullmoveCount: Number(fields[5] ?? 1),
    positionHistory: [],
    terminated: false,
    truncated: false,
    terminalReason: null,
    rewards: [0, 0],
  });
}

export function toFen(state: ChessState): string {
  const absolute = new Int8Array(64);
  for (let canonical = 0; canonical < 64; canonical += 1) {
    const square = canonicalToAbsolute(canonical, state.color);
    absolute[square] = state.color === 0 ? state.board[canonical]! : -state.board[canonical]!;
  }
  const symbols = ["", "p", "n", "b", "r", "q", "k"];
  const ranks: string[] = [];
  for (let rank = 7; rank >= 0; rank -= 1) {
    let text = "";
    let empty = 0;
    for (let file = 0; file < 8; file += 1) {
      const piece = absolute[file * 8 + rank]!;
      if (piece === 0) {
        empty += 1;
      } else {
        if (empty) text += String(empty);
        empty = 0;
        const symbol = symbols[Math.abs(piece)]!;
        text += piece > 0 ? symbol.toUpperCase() : symbol;
      }
    }
    if (empty) text += String(empty);
    ranks.push(text);
  }
  const mine = state.castlingRights;
  const absoluteRights = state.color === 0 ? mine : [mine[2], mine[3], mine[0], mine[1]];
  const rights = `${absoluteRights[1] ? "K" : ""}${absoluteRights[0] ? "Q" : ""}` +
    `${absoluteRights[3] ? "k" : ""}${absoluteRights[2] ? "q" : ""}` || "-";
  const ep = state.enPassant < 0
    ? "-"
    : squareName(canonicalToAbsolute(state.enPassant, state.color));
  return `${ranks.join("/")} ${state.color === 0 ? "w" : "b"} ${rights} ${ep} ${state.halfmoveCount} ${state.fullmoveCount}`;
}
